import { FiAlertTriangle, FiInfo } from "react-icons/fi";
import {
  adminLimitSourceLabel,
  cycleNumber,
  formatDay,
  formatShortDay,
  formatUsd,
  formatWeekRange,
  limitSourceLabel,
  percentUsed,
  resetDay,
} from "./usageFormat";

// Two cards over one week's fields, in the shape GET /me returns them (and
// check-token, and each history entry): this week against its quota, and this
// 28-day cycle against the monthly limit. forAdmin words them for an admin
// looking at someone else's usage instead of "you".

const CARD =
  "rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex flex-col gap-3";

// A bar from 0 to 100%. The fill is orange until a warning applies; the track
// is a lighter step of the same color so the scale reads across the whole bar.
function Meter({ percent, tone = "normal", label }) {
  const fill = {
    normal: "bg-orange-500",
    warning: "bg-amber-500",
    danger: "bg-red-500",
  }[tone];
  const track = {
    normal: "bg-orange-100 dark:bg-orange-900/30",
    warning: "bg-amber-100 dark:bg-amber-900/30",
    danger: "bg-red-100 dark:bg-red-900/30",
  }[tone];
  return (
    <div
      className={`h-2.5 w-full rounded-full overflow-hidden ${track}`}
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.min(percent, 100)}
      aria-label={label}
    >
      <div
        className={`h-full rounded-full transition-all duration-500 ${fill}`}
        style={{ width: `${Math.min(percent, 100)}%` }}
      />
    </div>
  );
}

// The headline amount and what it's measured against: "$0.42 of $3.50".
function Amount({ used, of, language, formatMoney }) {
  return (
    <p className="text-gray-900 dark:text-white">
      <span className="text-3xl font-bold">{formatMoney(used)}</span>
      {of !== null && of !== undefined && (
        <span className="ml-1.5 text-sm text-gray-500 dark:text-gray-400">
          {language === "zh" ? `／ ${formatMoney(of)}` : `of ${formatMoney(of)}`}
        </span>
      )}
    </p>
  );
}

function Notice({ tone, children }) {
  const styles = {
    info: "bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200",
    warning:
      "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200",
    danger:
      "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300",
  }[tone];
  const Icon = tone === "info" ? FiInfo : FiAlertTriangle;
  return (
    <p
      className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${styles}`}
    >
      <Icon className="w-4 h-4 shrink-0 mt-px" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

// This week against its quota. Being blocked is only mentioned when
// enforcement is on; while it's off the user just sees the quota is used.
export function WeekCard({
  week,
  enforced,
  language,
  forAdmin = false,
  formatMoney = formatUsd,
}) {
  const zh = language === "zh";
  const quota = week.this_week_quota_usd;
  const percent = percentUsed(week.used_usd, quota);
  const resets = formatDay(resetDay(week.week_end), language);
  const nearLimit =
    enforced && !week.over_quota && percent !== null && percent >= 80;
  const tone = !enforced
    ? "normal"
    : week.over_quota
      ? "danger"
      : nearLimit
        ? "warning"
        : "normal";

  return (
    <section className={CARD}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {zh ? "本週" : "This week"}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {formatWeekRange(week.week_start, week.week_end, language)}
          </p>
        </div>
        <p
          className="text-xs text-right text-gray-600 dark:text-gray-300"
          title={
            zh ? "於台北時間 00:00 重置" : "Resets at 00:00 Taipei time (UTC+8)"
          }
        >
          {zh
            ? `第 ${week.week_no} 週（共 4 週）· ${resets}重置`
            : `Week ${week.week_no} of 4 · resets ${resets}`}
        </p>
      </div>

      <Amount
        used={week.used_usd}
        of={quota}
        language={language}
        formatMoney={formatMoney}
      />

      {quota === null ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {zh
            ? "沒有適用的額度"
            : forAdmin
              ? "No limit applies"
              : "No limit applies to you"}
        </p>
      ) : (
        <>
          <Meter
            percent={percent}
            tone={tone}
            label={zh ? "本週額度使用率" : "This week's quota used"}
          />
          <div className="flex justify-between text-xs text-gray-600 dark:text-gray-300">
            <span>
              {zh
                ? `剩餘 ${formatMoney(week.remaining_usd)}`
                : `${formatMoney(week.remaining_usd)} left`}
            </span>
            <span className="tabular-nums">{percent}%</span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {zh
              ? `每週額度 ${formatMoney(week.monthly_limit_usd / 4)}`
              : `${formatMoney(week.monthly_limit_usd / 4)} weekly share`}
            {week.carried_in_usd > 0 &&
              (zh
                ? ` + 前幾週結轉 ${formatMoney(week.carried_in_usd)}`
                : ` + ${formatMoney(week.carried_in_usd)} carried over from earlier weeks`)}
          </p>
        </>
      )}

      <p className="text-xs text-gray-500 dark:text-gray-400">
        {zh
          ? `本週提問 ${week.turns} 次`
          : `${week.turns} ${week.turns === 1 ? "question" : "questions"} this week`}
      </p>

      {week.over_quota && enforced && (
        <Notice tone="danger">
          {forAdmin
            ? zh
              ? `已封鎖：已用完本週額度，將於${resets}恢復使用。`
              : `Blocked: this week's limit is reached. Available again on ${resets}.`
            : zh
              ? `您已用完本週額度。TG PIMS Agent 將於${resets}恢復使用。`
              : `You've reached this week's limit. TG PIMS Agent is available again on ${resets}.`}
        </Notice>
      )}
      {week.over_quota && !enforced && (
        <Notice tone="info">
          {zh
            ? `本週額度已用完，將於${resets}重置。`
            : `This week's quota has been used. It resets on ${resets}.`}
        </Notice>
      )}
      {nearLimit && (
        <Notice tone="warning">
          {zh
            ? `本週剩餘 ${formatMoney(week.remaining_usd)}。`
            : forAdmin
              ? `${formatMoney(week.remaining_usd)} left this week.`
              : `You have ${formatMoney(week.remaining_usd)} left this week.`}
        </Notice>
      )}
    </section>
  );
}

// This cycle's total against the monthly limit, and which limit applies.
// Nothing is enforced per cycle - only per week - so this bar never warns.
export function CycleCard({
  usage,
  language,
  forAdmin = false,
  formatMoney = formatUsd,
}) {
  const zh = language === "zh";
  const limit = usage.monthly_limit_usd;
  const percent = percentUsed(usage.cycle_used_usd, limit);

  return (
    <section className={CARD}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {zh ? "本月" : "This Month"}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {zh
              ? "每月額度以 28 天為一個週期"
              : "Monthly limit = one 28-day cycle"}
          </p>
        </div>
        <p className="text-xs text-right text-gray-600 dark:text-gray-300">
          {zh
            ? `第 ${cycleNumber(usage.cycle_start)} 週期 · `
            : `Cycle ${cycleNumber(usage.cycle_start)} · `}
          {formatShortDay(usage.cycle_start, language)} –{" "}
          {formatShortDay(usage.cycle_end, language)}
        </p>
      </div>

      <Amount
        used={usage.cycle_used_usd}
        of={limit}
        language={language}
        formatMoney={formatMoney}
      />

      {limit !== null && (
        <>
          <Meter
            percent={percent}
            label={zh ? "本週期額度使用率" : "This cycle's limit used"}
          />
          <div className="flex justify-between text-xs text-gray-600 dark:text-gray-300">
            <span>{zh ? "每月額度" : "Monthly limit"}</span>
            <span className="tabular-nums">{percent}%</span>
          </div>
        </>
      )}

      <p className="text-xs text-gray-500 dark:text-gray-400">
        {forAdmin
          ? adminLimitSourceLabel(usage.limit_source, language)
          : limitSourceLabel(usage.limit_source, language)}
      </p>
    </section>
  );
}
